import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { KeyValueDTO, PrincipalService } from '@ms/core';
import { Pageable } from '@ms/core/components/table/pageable.model';
import { Observable, first, of, switchMap, tap } from 'rxjs';
import { SchoolClass, SchoolClassList, SchoolClassSearch } from '../school-class/model/school-class.model';
import { httpParams, logRequest } from '../shared/http-utils';
import { IActiveDTO } from '../shared/model/active.model';
import { IPage } from '../shared/page/page.model';
import { SchoolClassStore } from '../store/school-class.store';
import { IPageableStudent, IStudent, IStudentSearch } from '../students';
import { StudentEnroll } from '../students/model/student-enroll';
import { StudentList } from '../students/transfer/student-selector/student-list.model';
import { SchoolClassForDailySearch } from './../daily-attendance/daily-attendance.model';

@Injectable({
    providedIn: 'root',
})
export class SchoolClassService {
    baseUrl = '/school-service/api/classes';
    private http = inject(HttpClient);
    private store = inject(SchoolClassStore);
    private principal = inject(PrincipalService);

    getActiveClassListKeyValue(searchDto: SchoolClassSearch, filter: boolean): Observable<KeyValueDTO[]> {
        if (!this.store.loaded()) {
            return this.search(searchDto).pipe(
                switchMap(() =>
                    of(this.store.getAllActiveKeyValue(searchDto.academicYearId, searchDto.gradeLevelId, filter)).pipe(
                        first()
                    )
                )
            );
        } else {
            return of(this.store.getAllActiveKeyValue(searchDto.academicYearId, searchDto.gradeLevelId, filter)).pipe(
                first()
            );
        }
    }

    getStudentsWithPagination(
        studentSearchDTO: IStudentSearch,
        params?: Pageable
    ): Observable<HttpResponse<IPage<IPageableStudent>>> {
        return this.http.post<IPage<IPageableStudent>>(`${this.baseUrl}/students/pageable`, studentSearchDTO, {
            observe: 'response',
            params: httpParams(params),
        });
    }

    addClassStudent(classId: string, studentList: string[], enroll?: StudentEnroll, entryDate?: Date): any {
        return this.http.post<IStudent[]>(
            `${this.baseUrl}/${classId}/students`,
            new StudentList(studentList, enroll, entryDate),
            {
                headers: logRequest(),
            }
        );
    }

    removeClassStudent(classId: string, studentId: string): any {
        return this.http.delete<IStudent[]>(`${this.baseUrl}/${classId}/students/${studentId}`, {
            headers: logRequest(),
        });
    }

    removeClassStudents(classId: string, studentIds: string[]): any {
        return this.http.put<IStudent[]>(`${this.baseUrl}/${classId}/students`, new StudentList(studentIds), {
            headers: logRequest(),
        });
    }

    get(id: string): Observable<SchoolClassList> {
        if (!this.store.loaded()) {
            const schoolId = this.principal.authenticatedUser.activeSchool.id;
            return this.search({ schoolId, filter: false }).pipe(switchMap(() => of(this.store.get(id)).pipe(first())));
        } else {
            return of(this.store.get(id)).pipe(first());
        }
    }

    // Dont use store, for update using
    getById(id: string): Observable<HttpResponse<SchoolClass>> {
        return this.http.get<SchoolClass>(`${this.baseUrl}/${id}`, {
            observe: 'response',
        });
    }

    getStudentClassByAcadPeriod(studentId: string, acadPeriodId: string): Observable<HttpResponse<SchoolClass>> {
        return this.http.get<SchoolClass>(`${this.baseUrl}/student/${studentId}/period/${acadPeriodId}`, {
            observe: 'response',
        });
    }

    create(schoolClass: SchoolClass): Observable<HttpResponse<SchoolClassList>> {
        return this.http
            .post<SchoolClassList>(this.baseUrl, schoolClass, {
                observe: 'response',
                headers: logRequest(),
            })
            .pipe(
                tap(result => {
                    this.store.create(result.body);
                })
            );
    }

    changeActive(status: IActiveDTO): Observable<HttpResponse<any>> {
        return this.http
            .put<IActiveDTO>(`${this.baseUrl}/active-change`, status, {
                observe: 'response',
                headers: logRequest(),
            })
            .pipe(tap(() => this.store.setActive(status.id, status.active)));
    }

    update(schoolClass: SchoolClass): Observable<HttpResponse<SchoolClassList>> {
        return this.http
            .put<SchoolClassList>(this.baseUrl, schoolClass, {
                observe: 'response',
                headers: logRequest(),
            })
            .pipe(
                tap(result => {
                    this.store.update(result.body);
                })
            );
    }

    delete(id: string): Observable<HttpResponse<any>> {
        return this.http
            .delete(`${this.baseUrl}/${id}`, {
                observe: 'response',
                headers: logRequest(),
            })
            .pipe(tap(() => this.store.remove(id)));
    }

    search(search: SchoolClassSearch): Observable<SchoolClassList[]> {
        if (!this.store.loaded()) {
            return this.http.post<SchoolClassList[]>(`${this.baseUrl}/search`, { schoolId: search.schoolId }).pipe(
                tap(result => this.store.set(result)),
                switchMap(() => of(this.store.findAll(search)).pipe(first()))
            );
        } else {
            return of(this.store.findAll(search)).pipe(first());
        }
    }

    getClassesForDailyAttendance(dto: SchoolClassForDailySearch): Observable<HttpResponse<KeyValueDTO[]>> {
        return this.http.post<KeyValueDTO[]>('/school-service/api/classes/daily-attendance', dto, {
            observe: 'response',
        });
    }

    getAllActiveClassesBySchoolIdKeyValueDTO(
        schoolId: string,
        academicYearId: string,
        filter: boolean
    ): Observable<KeyValueDTO[]> {
        if (!this.store.loaded()) {
            return this.search({
                schoolId,
            }).pipe(
                tap(result => this.store.set(result)),
                switchMap(() =>
                    of(
                        this.store
                            .findAll({ academicYearId, filter })
                            .map(i => new KeyValueDTO(i.id, i.name, i.gradeLevelId, i.teacherId, i.gradeLevelName))
                    ).pipe(first())
                )
            );
        } else {
            return of(
                this.store
                    .findAll({ academicYearId, filter })
                    .map(i => new KeyValueDTO(i.id, i.name, i.gradeLevelId, i.teacherId, i.gradeLevelName))
            ).pipe(first());
        }
    }
}
